// Fallback returns the raw 3-letter code so the UI never silently
// misrepresents the amount (no blanket "€" for unknown currencies).

const currencies = {
  // ── Euro area ─────────────────────────────────────────────────────
  EUR: '€',

  // ── Major world ───────────────────────────────────────────────────
  USD: '$',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  CHF: 'CHF',

  // ── Rest of Europe (non-euro) ─────────────────────────────────────
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  ISK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RSD: 'дин',
  ALL: 'L',
  MKD: 'ден',
  BAM: 'KM',
  MDL: 'L',
  UAH: '₴',
  BYN: 'Br',
  GEL: '₾',
  AMD: '֏',
  AZN: '₼',
  RUB: '₽',
  TRY: '₺',

  // ── Middle East / North Africa ────────────────────────────────────
  ILS: '₪',
  AED: 'د.إ',
  SAR: '﷼',
  QAR: '﷼',
  KWD: 'د.ك',
  BHD: '.د.ب',
  OMR: '﷼',
  JOD: 'د.ا',
  LBP: 'ل.ل',
  IRR: '﷼',
  IQD: 'ع.د',
  EGP: 'E£',
  MAD: 'د.م.',
  TND: 'د.ت',
  DZD: 'د.ج',
  LYD: 'ل.د',

  // ── Sub-Saharan Africa ────────────────────────────────────────────
  NGN: '₦',
  GHS: '₵',
  KES: 'KSh',
  UGX: 'USh',
  TZS: 'TSh',
  ZAR: 'R',
  BWP: 'P',
  MUR: '₨',
  ETB: 'Br',
  XOF: 'CFA',
  XAF: 'FCFA',

  // ── Indian subcontinent ───────────────────────────────────────────
  INR: '₹',
  PKR: '₨',
  LKR: 'Rs',
  BDT: '৳',
  NPR: '₨',

  // ── East & Southeast Asia ─────────────────────────────────────────
  HKD: 'HK$',
  TWD: 'NT$',
  KRW: '₩',
  SGD: 'S$',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  THB: '฿',
  VND: '₫',
  MMK: 'K',
  KHR: '៛',
  LAK: '₭',
  MNT: '₮',
  KZT: '₸',
  UZS: 'soʻm',

  // ── Oceania ───────────────────────────────────────────────────────
  AUD: 'A$',
  NZD: 'NZ$',
  FJD: 'FJ$',

  // ── North America ─────────────────────────────────────────────────
  CAD: 'C$',
  MXN: 'MX$',

  // ── Central / South America & Caribbean ───────────────────────────
  BRL: 'R$',
  ARS: 'AR$',
  CLP: 'CLP$',
  COP: 'COL$',
  PEN: 'S/',
  UYU: '$U',
  VES: 'Bs.',
  BOB: 'Bs.',
  PYG: '₲',
  GTQ: 'Q',
  HNL: 'L',
  NIO: 'C$',
  CRC: '₡',
  DOP: 'RD$',
  JMD: 'J$',
  TTD: 'TT$',
  BBD: 'Bds$',
  BSD: 'B$',
  XCD: 'EC$',

  // ── Precious-metal reference codes ────────────────────────────────
  XAU: 'oz t',
  XAG: 'oz t',
}

/**
 * Returns the glyph for a known currency code, otherwise the raw
 * uppercase code so the UI stays honest when a currency is unrecognised.
 * @param {string} code ISO 4217 currency code.
 * @returns {string}
 */
export const currency_symbol = (code) => {
  if (typeof code !== 'string' || code.length === 0) {
    return ''
  }
  const key = code.toUpperCase()
  return currencies[key] || key
}

// ISO 4217 minor-unit count per currency: JPY/KRW/VND/etc. → 0 (no
// fractional unit); KWD/BHD/IQD/etc. → 3 (dinar precision); else 2.
const ZERO_DECIMAL = {
  JPY: true, KRW: true, VND: true, IDR: true, CLP: true, PYG: true,
  RWF: true, UGX: true, BIF: true, DJF: true, GNF: true, KMF: true,
  XAF: true, XOF: true, XPF: true, ISK: true, HUF: true,
}
const THREE_DECIMAL = {
  KWD: true, BHD: true, IQD: true, JOD: true, OMR: true, TND: true, LYD: true,
}

/**
 * Decimal places to render for `code`. Defaults to 2 for unknown codes.
 * @param {string} code ISO 4217 currency code.
 * @returns {number}
 */
export const currency_decimals = (code) => {
  if (typeof code !== 'string' || code.length === 0) {
    return 2
  }
  const key = code.toUpperCase()
  if (ZERO_DECIMAL[key]) return 0
  if (THREE_DECIMAL[key]) return 3
  return 2
}

export default currencies
